import type { PortfolioCard } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router';
import { portfolioDetailPath } from '@/shared/config';
import { Button, cn, ImageWithFallback } from '@/shared/ui';

interface PortfolioCaseCardProps {
  readonly item: PortfolioCard;
  /** 깨진 대표사진을 다시 불러올 때 서명 URL 을 새로 받도록 목록을 다시 조회한다. */
  readonly onRetry?: () => void;
  /**
   * 목록 맨 앞의 큰 카드 — 격자에서 두 칸·두 줄을 차지하므로 사진이 남는 높이를 채운다.
   * 좁은 화면(두 칸 전폭)에서는 사진을 16:9 로 낮춰 화면을 덮지 않게 한다.
   */
  readonly isFeatured?: boolean;
  /** 캐러셀 안에서는 브라우저 기본 이미지 끌기가 캐러셀 끌기를 가로채지 않게 끈다. */
  readonly draggable?: boolean;
}

/**
 * 포트폴리오 사례 카드 — 각진 회색 사진 칸(올리면 살짝 확대) 아래 카테고리·굵은 제목,
 * 얇은 선 아래 고객사명과 "사례 보기 →" 안내. 사진·글 전체가 사례 상세로 가는 단일 링크이고,
 * 사진이 깨지면 링크 밖에 "사진 다시 불러오기"를 둔다(링크 안 버튼 중첩 금지).
 */
export const PortfolioCaseCard = ({
  item,
  onRetry,
  isFeatured = false,
  draggable,
}: PortfolioCaseCardProps): ReactElement => {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const categoryNames = item.categories.map((category) => category.name).join(' · ');

  return (
    <article className="flex h-full min-w-0 flex-col gap-3">
      <Link
        to={portfolioDetailPath(item.id)}
        aria-label={item.title}
        className="group flex flex-1 flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4"
      >
        <div
          className={cn(
            'flex aspect-[4/3] w-full items-center justify-center overflow-hidden bg-muted/70',
            isFeatured && 'sm:aspect-[16/9] lg:aspect-auto lg:min-h-0 lg:flex-1',
          )}
        >
          <ImageWithFallback
            key={`${item.imageUrl}:${attempt}`}
            src={item.imageUrl}
            alt={item.title}
            draggable={draggable}
            className="size-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
            failureText="사진을 불러오지 못했습니다."
            onError={() => setFailedUrl(item.imageUrl)}
          />
        </div>

        <div className="flex flex-col pt-5">
          <p className="text-sm text-muted-foreground wrap-anywhere">{categoryNames}</p>
          <h3
            className={cn(
              'mt-2 text-xl leading-snug font-bold tracking-[-0.02em] wrap-anywhere sm:text-2xl',
              isFeatured && 'lg:text-[clamp(1.75rem,2.4vw,2.25rem)] lg:leading-tight',
            )}
          >
            {item.title}
          </h3>
          <div className="mt-5 flex items-center justify-between gap-3 border-t border-foreground/10 pt-4">
            <span className="min-w-0 truncate text-sm text-muted-foreground">
              {item.clientName ?? ''}
            </span>
            <span className="text-sm font-semibold whitespace-nowrap">
              사례 보기{' '}
              <span
                aria-hidden
                className="inline-block transition-transform group-hover:translate-x-1 motion-reduce:transition-none"
              >
                →
              </span>
            </span>
          </div>
        </div>
      </Link>
      {failedUrl === item.imageUrl && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => {
            setFailedUrl(null);
            setAttempt((value) => value + 1);
            onRetry?.();
          }}
        >
          사진 다시 불러오기
        </Button>
      )}
    </article>
  );
};
